import {
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from "react";

type Search = Record<string, unknown> | undefined;

function buildHref(to: string, search?: Search): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search ?? {}))
    if (value !== undefined && value !== null && value !== "")
      params.set(key, String(value));
  const query = params.toString();
  return query ? `${to}?${query}` : to;
}

/**
 * SPA navigation that keeps the app shell in sync.
 *
 * PayDay does not use a routing framework: `src/app.tsx` derives the active
 * page from `location.pathname` and re-renders on the `popstate` event. To
 * navigate we push a new history entry and dispatch `popstate` so the shell
 * picks the new page up — mirroring browser back/forward behaviour.
 */
export function navigateTo(to: string, search?: Search, state?: Record<string, unknown>): void {
  const href = buildHref(to, search);
  if (
    href === `${window.location.pathname}${window.location.search}` &&
    window.location.hash === ""
  )
    return;
  window.history.pushState(state ?? {}, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function Link({
  to,
  search,
  state,
  children,
  onClick,
  ...props
}: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  to: string;
  search?: Search;
  state?: Record<string, unknown>;
  children: ReactNode;
}) {
  return (
    <a
      href={buildHref(to, search)}
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        // Let modified clicks (new tab / new window) fall through to the
        // default anchor behaviour.
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        )
          return;
        event.preventDefault();
        onClick?.(event);
        navigateTo(to, search, state);
      }}
      {...props}
    >
      {children}
    </a>
  );
}

/** Read route params from the current path (e.g. `/work-profiles/:id`). */
export function useParams(): { employeeId?: string } {
  const match = /^\/work-profiles\/([^/]+)$/u.exec(window.location.pathname);
  return match?.[1]
    ? { employeeId: decodeURIComponent(match[1]) }
    : {};
}
